import { Card, CardContent } from '@/components/ui/card'
import { BaseFilterSection } from '@/components/queries/BaseFilterSection'
import type { FilterControlConfig, BaseFilterProps } from '@/types/filters'
import type { Project, Task, Document } from '@/types/nodes'

/**
 * BaseFiltersContainer Component
 *
 * A reusable container component that handles the layout and rendering of filter controls.
 * Supports collapsible display and responsive grid layouts.
 */
export function BaseFiltersContainer<T extends Record<string, string | number | symbol>>({
  filters,
  onFiltersChange,
  availableUsers = [],
  availableWorkspaces = [],
  className,
  collapsed = false,
  filterConfigs,
  gridCols = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  availableProjects = [],
  availableTasks = [],
  availableDocuments = [],
}: BaseFilterProps<T> & {
  filterConfigs?: FilterControlConfig<T>[]
  gridCols?: string
  availableProjects?: Project[]
  availableTasks?: Task[]
  availableDocuments?: Document[]
}) {
  if (collapsed || !filterConfigs) {
    return null
  }

  return (
    <Card className={className}>
      <CardContent className="pt-6">
        <div className={`grid ${gridCols} gap-4`}>
          {filterConfigs.map((config) => (
            <BaseFilterSection
              key={String(config.key)}
              config={config}
              value={filters[config.key]}
              onChange={(value: string) => onFiltersChange({ [config.key]: value } as Partial<T>)}
              availableUsers={availableUsers}
              availableWorkspaces={availableWorkspaces}
              availableProjects={availableProjects}
              availableTasks={availableTasks}
              availableDocuments={availableDocuments}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
